export const PENDING_MAP_LOCATION_KEY = "pendingMapLocation";
export const FISHIPEDIA_MAP_RESTORE_TIMEOUT_MS = 12000;

const MIN_LATITUDE = -90;
const MAX_LATITUDE = 90;
const MIN_LONGITUDE = -180;
const MAX_LONGITUDE = 180;
const MIN_MAP_ZOOM = 2;
const MAX_MAP_ZOOM = 18;
const cancelledRestoreAttempts = new Set();
const pendingCancellationTimers = new Map();

function isObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isValidRestoreAttemptId(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function isNumberInRange(value, minimum, maximum) {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= minimum &&
    value <= maximum
  );
}

function buildPendingMapRestoreRecord(payload, restoreAttemptId) {
  if (
    !isObject(payload) ||
    !isValidRestoreAttemptId(restoreAttemptId) ||
    !isNumberInRange(payload.lat, MIN_LATITUDE, MAX_LATITUDE) ||
    !isNumberInRange(payload.lng, MIN_LONGITUDE, MAX_LONGITUDE) ||
    !isNumberInRange(payload.zoom, MIN_MAP_ZOOM, MAX_MAP_ZOOM)
  ) {
    return null;
  }

  return {
    lat: payload.lat,
    lng: payload.lng,
    zoom: payload.zoom,
    openOverlay: true,
    tab: "fish",
    fishipediaRestore: true,
    restoreAttemptId,
  };
}

/**
 * Writes only the minimal, validated Fishipedia map-restore record. The attempt
 * ID may be supplied separately or on the payload for backwards-compatible
 * call sites while they adopt ownership-aware cleanup.
 */
export function writePendingMapRestore(
  storage,
  payload,
  restoreAttemptId = payload?.restoreAttemptId
) {
  const record = buildPendingMapRestoreRecord(payload, restoreAttemptId);
  if (!record) return false;

  try {
    storage.setItem(PENDING_MAP_LOCATION_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads a Fishipedia-owned record only when it belongs to the current live
 * restore attempt. Rejected results never expose a flyable record.
 */
export function readPendingMapRestore(storage, liveRestoreAttemptId) {
  let raw;
  try {
    raw = storage.getItem(PENDING_MAP_LOCATION_KEY);
  } catch {
    return { status: "unavailable", record: null, raw: null };
  }

  if (typeof raw !== "string" || raw.length === 0) {
    return { status: "missing", record: null, raw: null };
  }

  let candidate;
  try {
    candidate = JSON.parse(raw);
  } catch {
    return { status: "invalid", record: null, raw };
  }

  if (!isObject(candidate) || candidate.fishipediaRestore !== true) {
    return { status: "not-fishipedia", record: null, raw };
  }

  if (!isValidRestoreAttemptId(candidate.restoreAttemptId)) {
    return { status: "invalid", record: null, raw };
  }

  if (
    !isValidRestoreAttemptId(liveRestoreAttemptId) ||
    candidate.restoreAttemptId !== liveRestoreAttemptId
  ) {
    return { status: "stale", record: null, raw };
  }

  const record = buildPendingMapRestoreRecord(
    candidate,
    liveRestoreAttemptId
  );
  if (!record) {
    return { status: "invalid", record: null, raw };
  }

  return { status: "accepted", record, raw };
}

/**
 * Removes the shared-key record only if it still belongs to the supplied
 * attempt. The second read protects a newer write from an older cleanup.
 */
export function clearPendingMapRestore(storage, restoreAttemptId) {
  if (!isValidRestoreAttemptId(restoreAttemptId)) return false;

  try {
    const raw = storage.getItem(PENDING_MAP_LOCATION_KEY);
    if (typeof raw !== "string" || raw.length === 0) return false;

    const candidate = JSON.parse(raw);
    if (
      !isObject(candidate) ||
      candidate.fishipediaRestore !== true ||
      candidate.restoreAttemptId !== restoreAttemptId
    ) {
      return false;
    }

    if (storage.getItem(PENDING_MAP_LOCATION_KEY) !== raw) return false;

    storage.removeItem(PENDING_MAP_LOCATION_KEY);
    return true;
  } catch {
    // Storage can be disabled. Ownership still becomes harmless when the live
    // in-memory attempt is released because future reads require a token match.
    return false;
  }
}

export function scheduleMapRestoreTimeout(timerApi, onTimeout) {
  const timerId = timerApi.setTimeout(
    onTimeout,
    FISHIPEDIA_MAP_RESTORE_TIMEOUT_MS
  );
  return () => timerApi.clearTimeout(timerId);
}

export function resumeMapRestoreAttempt(timerApi, restoreAttemptId) {
  if (!isValidRestoreAttemptId(restoreAttemptId)) return false;
  const timerId = pendingCancellationTimers.get(restoreAttemptId);
  if (timerId !== undefined) {
    timerApi.clearTimeout(timerId);
    pendingCancellationTimers.delete(restoreAttemptId);
  }
  return !cancelledRestoreAttempts.has(restoreAttemptId);
}

export function deferMapRestoreCancellation(
  timerApi,
  restoreAttemptId,
  onCancel = null
) {
  if (!isValidRestoreAttemptId(restoreAttemptId)) return;
  const previousTimerId = pendingCancellationTimers.get(restoreAttemptId);
  if (previousTimerId !== undefined) timerApi.clearTimeout(previousTimerId);

  const timerId = timerApi.setTimeout(() => {
    pendingCancellationTimers.delete(restoreAttemptId);
    cancelledRestoreAttempts.add(restoreAttemptId);
    onCancel?.();
  }, 0);
  pendingCancellationTimers.set(restoreAttemptId, timerId);
}
